import { cn } from "@/lib/utils";

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

interface FooterProps {
  /** Newsletter section (left side) */
  newsletter?: React.ReactNode;
  /** Link columns (right side) */
  columns?: React.ReactNode;
  /** Credits section (bottom) */
  credits?: React.ReactNode;
  /** HTML id attribute */
  id?: string;
  /** Additional class names */
  className?: string;
}

interface FooterNewsletterProps {
  /** Logo element */
  logo?: React.ReactNode;
  /** Description text */
  description?: string;
  /** Email input placeholder */
  placeholder?: string;
  /** Submit button text */
  buttonText?: string;
  /** Privacy notice text */
  privacyNotice?: React.ReactNode;
  /** Form submit handler */
  onSubmit?: (email: string) => void | Promise<void>;
  /** Whether a submission is in progress */
  isLoading?: boolean;
  /** Additional class names */
  className?: string;
}

interface FooterColumnProps {
  /** Column title */
  title: string;
  /** Column links or content */
  children: React.ReactNode;
  /** Additional class names */
  className?: string;
}

interface FooterLinkProps {
  /** Link destination */
  href: string;
  /** Link text */
  children: React.ReactNode;
  /** Icon element (for social links) */
  icon?: React.ReactNode;
  /** Open in new tab */
  external?: boolean;
  /** Additional class names */
  className?: string;
}

interface FooterCreditsProps {
  /** Copyright text */
  copyright?: React.ReactNode;
  /** Legal links */
  links?: React.ReactNode;
  /** Additional class names */
  className?: string;
}

/* -------------------------------------------------------------------------- */
/*                                 Components                                 */
/* -------------------------------------------------------------------------- */

/**
 * Footer - Site footer with newsletter, link columns, and credits
 *
 * Desktop Layout:
 * - Footer: centered, 80px padding, 80px gap
 * - Container: max-width 1280px
 * - Content: Newsletter (500px) + Links (flex-1) with 128px gap
 * - Links: 3 columns with 40px gap
 * - Credits: copyright left, legal links right
 *
 * Mobile Layout:
 * - 48px padding, 48px gap
 * - Everything stacks vertically
 */
export function Footer({
  newsletter,
  columns,
  credits,
  id,
  className,
}: FooterProps) {
  return (
    <footer
      id={id}
      className={cn(
        "w-full",
        "flex flex-col",
        "px-px-mobile md:px-px-tablet lg:px-px-desktop",
        "py-footer-y-mobile lg:py-footer-y",
        className
      )}
    >
      {/* Container */}
      <div className="w-full flex flex-col gap-gap-section-mobile lg:gap-gap-section">
        {/* Content: Newsletter + Columns */}
        <div className="flex flex-col lg:flex-row gap-gap-section-mobile lg:gap-32">
          {newsletter}

          {columns && (
            <div className="flex-1 flex flex-col lg:flex-row gap-10">
              {columns}
            </div>
          )}
        </div>

        {/* Credits */}
        {credits}
      </div>
    </footer>
  );
}

/**
 * FooterNewsletter - Newsletter signup section with logo, description, and form
 */
export function FooterNewsletter({
  logo,
  description,
  placeholder = "Enter your email",
  buttonText = "Subscribe",
  privacyNotice,
  onSubmit,
  isLoading,
  className,
}: FooterNewsletterProps) {
  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const email = formData.get("email") as string;
    if (!email) return;
    await onSubmit?.(email);
    form.reset();
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-5 lg:w-[500px] shrink-0",
        className
      )}
    >
      {logo}

      {description && (
        <p className="text-base leading-[150%]">
          {description}
        </p>
      )}

      <div className="flex flex-col gap-3">
        <form
          onSubmit={handleSubmit}
          className="flex flex-col md:flex-row gap-4"
        >
          <input
            type="email"
            name="email"
            id="footer-email-input"
            placeholder={placeholder}
            className={cn(
              "flex-1 h-10 px-3 py-2",
              "text-base leading-[150%] placeholder:opacity-60",
              "bg-transparent border border-white/20 rounded-xl",
              "focus:outline-none focus:border-white/40"
            )}
          />
          <button
            type="submit"
            disabled={isLoading}
            className={cn(
              "h-11 px-6 w-full md:w-auto",
              "text-base font-medium leading-[150%]",
              "border border-white/20 rounded-full",
              "hover:bg-white/10 transition-colors",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          >
            {buttonText}
          </button>
        </form>

        {privacyNotice && (
          <p className="text-xs leading-[150%]">
            {privacyNotice}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * FooterColumn - A column of links in the footer
 * Desktop: 16px gap between title and links, columns grow equally
 */
export function FooterColumn({
  title,
  children,
  className,
}: FooterColumnProps) {
  return (
    <div
      className={cn(
        "flex flex-col gap-3 lg:gap-4 flex-1",
        className
      )}
    >
      <h3 className="text-base font-semibold leading-[150%]">
        {title}
      </h3>
      <ul className="flex flex-col">
        {children}
      </ul>
    </div>
  );
}

/**
 * FooterLink - A link item within a footer column
 */
export function FooterLink({
  href,
  children,
  icon,
  external,
  className,
}: FooterLinkProps) {
  return (
    <li>
      <a
        href={href}
        className={cn(
          "flex items-center gap-3 py-2",
          "text-sm leading-[150%]",
          "hover:opacity-80 transition-opacity",
          className
        )}
        {...(external && {
          target: "_blank",
          rel: "noopener noreferrer",
        })}
      >
        {icon && (
          <span className="w-6 h-6 flex items-center justify-center">
            {icon}
          </span>
        )}
        {children}
      </a>
    </li>
  );
}

/**
 * FooterCredits - Bottom section with copyright and legal links
 * Desktop: 32px gap after divider, row with copyright left, links right
 * Mobile: stacked with links above copyright
 */
export function FooterCredits({
  copyright,
  links,
  className,
}: FooterCreditsProps) {
  return (
    <div
      className={cn(
        "flex flex-col gap-6 lg:gap-8",
        className
      )}
    >
      {/* Divider */}
      <div className="w-full h-px bg-white/20" />

      {/* Row */}
      <div className="flex flex-col lg:flex-row lg:justify-between items-start lg:items-start gap-8 lg:gap-16">
        {/* Mobile: links first, Desktop: copyright first */}
        {links && (
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6 order-first lg:order-last">
            {links}
          </div>
        )}

        {copyright && (
          <span className="text-sm leading-[150%]">
            {copyright}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * FooterCreditsLink - A link in the credits section (underlined)
 */
export function FooterCreditsLink({
  href,
  children,
  external,
  className,
}: Omit<FooterLinkProps, "icon">) {
  return (
    <a
      href={href}
      className={cn(
        "text-sm leading-[150%] underline",
        "hover:opacity-80 transition-opacity",
        className
      )}
      {...(external && {
        target: "_blank",
        rel: "noopener noreferrer",
      })}
    >
      {children}
    </a>
  );
}
